import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Camera, Clock, LogIn, LogOut } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { hora, nomeDeArquivo, nomePessoa } from '../../nucleo/formato';
import { COR_ORIGEM, horas, invalidarPonto, type Hoje } from './tipos';

const FOTO_MAX = 512 * 1024;

/** Localização do navegador (com a precisão); nula quando a pessoa não deixa ou o aparelho não tem. */
function lerLocalizacao(): Promise<{ latitude: number; longitude: number; precisao_m: number } | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolver) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolver({ latitude: p.coords.latitude, longitude: p.coords.longitude, precisao_m: Math.round(p.coords.accuracy) }),
      () => resolver(null),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}

const paraBase64 = (f: File) =>
  new Promise<string>((resolver, rejeitar) => {
    const r = new FileReader();
    r.onload = () => resolver(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => rejeitar(r.error);
    r.readAsDataURL(f);
  });

/** Marcar ponto pela tela (o app do funcionário usa a mesma API): hora do servidor, marcações de hoje e a política que vale. */
export function Marcar() {
  const { t } = useTranslation('ponto');
  const qc = useQueryClient();
  const toast = useToast();
  const arquivo = useRef<HTMLInputElement>(null);
  const [foto, setFoto] = useState<File | null>(null);
  const q = useQuery({ queryKey: ['ponto', 'hoje'], queryFn: () => get<Hoje>('/ponto/hoje'), refetchInterval: 60_000 });
  const marcar = useMutation({
    mutationFn: async (h: Hoje) => {
      const corpo: Record<string, unknown> = { id: crypto.randomUUID(), origem: 'web', dispositivo: navigator.userAgent.slice(0, 200) };
      if (h.politica?.exige_localizacao || h.politica?.cerca) {
        const local = await lerLocalizacao();
        if (!local) throw new Error(t('aviso.semLocalizacao'));
        Object.assign(corpo, local);
      }
      if (foto) corpo.foto = await paraBase64(foto);
      return post<{ momento: string }>('/ponto/marcacoes', corpo);
    },
    onSuccess: async (r) => {
      setFoto(null);
      await invalidarPonto(qc);
      toast.sucesso(t('marcado', { hora: hora(r.momento) }));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Estado consulta={q}>
      {(h) => {
        if (!h.funcionario) return <Vazio texto={t('vazio.semFuncionario')} />;
        if (!h.bate_ponto) return <Vazio texto={t('vazio.semPonto')} />;
        const pedeFoto = !!h.politica?.exige_foto;
        const entrada = h.proxima === 'entrada';
        return (
          <>
            <AcaoPrimaria>
              <Botao
                variante="primario"
                icone={entrada ? <LogIn className="size-4" /> : <LogOut className="size-4" />}
                carregando={marcar.isPending}
                aria-disabled={pedeFoto && !foto}
                onClick={() => (pedeFoto && !foto ? toast.info(t('aviso.foto')) : marcar.mutate(h))}
              >
                {entrada ? t('acoes.marcarEntrada') : t('acoes.marcarSaida')}
              </Botao>
            </AcaoPrimaria>
            <div className="grid min-w-0 gap-4 md:grid-cols-2">
              <Cartao titulo={nomePessoa(h.funcionario.nome)}>
                <dl className="text-sm">
                  <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                    <dt className="text-tinta-3">{t('campos.agora')}</dt>
                    <dd className="numero flex items-center gap-1 text-tinta-1">
                      <Clock className="size-4 text-tinta-3" aria-hidden />
                      {hora(h.agora)}
                    </dd>
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                    <dt className="text-tinta-3">{t('campos.horario')}</dt>
                    <dd className="min-w-0 text-right text-tinta-1">
                      {h.horario
                        ? `${t('horarioDoDia', { entrada: h.horario.entrada, saida: h.horario.saida })}${h.horario.intervalo_inicio ? `, ${t('intervaloDoDia', { inicio: h.horario.intervalo_inicio, fim: h.horario.intervalo_fim })}` : ''}`
                        : t('semHorario')}
                    </dd>
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                    <dt className="text-tinta-3">{t('campos.trabalhado')}</dt>
                    <dd className="numero text-tinta-1">{t('trabalhadoHoje', { trabalhado: horas(h.trabalhado_min), esperado: horas(h.esperado_min) })}</dd>
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 py-2">
                    <dt className="text-tinta-3">{t('campos.proxima')}</dt>
                    <dd className="text-tinta-1">{t(`proxima.${h.proxima}`)}</dd>
                  </div>
                </dl>
                {(h.politica?.exige_localizacao || h.politica?.cerca || pedeFoto) && (
                  <ul className="mt-3 flex flex-col gap-1 text-sm text-tinta-3">
                    {(h.politica?.exige_localizacao || h.politica?.cerca) && <li>{t('aviso.localizacao')}</li>}
                    {h.politica?.cerca && <li>{t('aviso.cerca')}</li>}
                    {pedeFoto && <li>{t('aviso.foto')}</li>}
                  </ul>
                )}
                {pedeFoto && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      ref={arquivo}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="user"
                      className="sr-only"
                      tabIndex={-1}
                      aria-hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        e.target.value = '';
                        if (f && f.size > FOTO_MAX) return toast.erro(t('aviso.fotoGrande'));
                        setFoto(f);
                      }}
                    />
                    <Botao icone={<Camera className="size-4" />} onClick={() => arquivo.current?.click()}>
                      {foto ? t('acoes.trocarFoto') : t('acoes.escolherFoto')}
                    </Botao>
                    {foto && (
                      <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeDeArquivo(foto.name)}>
                        {nomeDeArquivo(foto.name)}
                      </span>
                    )}
                  </div>
                )}
              </Cartao>
              <Cartao titulo={t('campos.marcacoes')}>
                {h.marcacoes.length === 0 ? (
                  <p className="text-sm text-tinta-3">{t('nenhumaHoje')}</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {h.marcacoes.map((m, i) => (
                      <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-borda pb-2 text-sm last:border-0">
                        <span className="text-tinta-2">{t(`proxima.${i % 2 === 0 ? 'entrada' : 'saida'}`)}</span>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="numero text-tinta-1">{m.hora}</span>
                          <Selo cor={COR_ORIGEM[m.origem]}>{t(`origem.${m.origem}`)}</Selo>
                          {m.fora_de_linha && <Selo cor="laranja">{t('campos.foraDeLinha')}</Selo>}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </Cartao>
            </div>
          </>
        );
      }}
    </Estado>
  );
}
